import { Input } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { asSpelled, FilterChip, SelectChip, type ICompactChip } from '@/design';
import { ParamChip } from '../pages/sec-adv/firm-filters';
import { RadioFilter } from './admin-page';
import {
  DISCLOSURE_STATES,
  liveAccountFilter,
  optionLabel,
  REGISTRY_STATES,
  VERIFICATIONS,
  withUnknownOption,
  type IFilterOption
} from './record-filters';

/**
 * The filters People, Teams and Firms no longer offer (SR-401's AC1; the owner's ruling of
 * 2026-10-15): Numbers, BrokerCheck, Account, Disclosures and Created. No requirement names any
 * of them — the client said of People's that nobody was sure what they were — and each list now
 * offers only the filters of its own record type. survey-api still reads every one, and the search
 * is the URL, so **a chip is drawn while the address names its parameter**: an old link stays
 * readable and clearable, and its "Any" position takes the chip away with the parameter — the
 * precedent of the team filters on People (the record search plan's D9). Nothing narrows a list
 * without a chip saying so. As with those, the control a clear was chosen in goes with the chip,
 * and the focus with it — accepted, since only an old link draws one.
 */

/** The URL's parameters these chips stand for, in the order they are drawn. */
export const RETIRED_FILTERS = [
  'verification',
  'registry',
  'account',
  'disclosures',
  'createdSince'
] as const;

export function RetiredFilterChips({
  params,
  choose,
  accountStates,
  name
}: {
  params: URLSearchParams;
  choose: (name: string) => (value: Nullable<string>) => void;
  /** People's account states, or a record's — People's alone hold "Not registered". */
  accountStates: readonly IFilterOption[];
  /** The page's name, which keeps the radios' `name` unique. */
  name: string;
}) {
  const verification = params.get('verification');
  const registry = params.get('registry');
  // A retired password state is read as no filter (SR-476), so it draws no chip either.
  const account = liveAccountFilter(params.get('account'));
  const disclosures = params.get('disclosures');
  const createdSince = params.get('createdSince');
  return (
    <>
      {verification !== null && (
        <FilterChip label="Numbers" summary={optionLabel(VERIFICATIONS, verification)}>
          <RadioFilter
            label="Numbers"
            name={`${name}-verification`}
            options={withUnknownOption(VERIFICATIONS, verification)}
            value={verification}
            onChoose={choose('verification')}
          />
        </FilterChip>
      )}
      {registry !== null && (
        <SelectChip
          label="BrokerCheck"
          any="Any answer"
          value={registry}
          options={REGISTRY_STATES}
          onChoose={choose('registry')}
        />
      )}
      {account !== null && (
        <SelectChip
          label="Account"
          any="Any account"
          value={account}
          options={accountStates}
          onChoose={choose('account')}
        />
      )}
      {disclosures !== null && (
        <SelectChip
          label="Disclosures"
          any="Any state"
          value={disclosures}
          options={DISCLOSURE_STATES}
          onChoose={choose('disclosures')}
        />
      )}
      {createdSince !== null && (
        <FilterChip
          label="Created"
          summary={createdSince === '' ? asSpelled(createdSince) : `since ${createdSince}`}
        >
          <label className="flex items-center gap-2">
            <span className="text-muted-foreground">Created since</span>
            <Input
              type="date"
              className="w-40"
              value={createdSince}
              onChange={(event) => {
                // A half-typed date reads as '' like an emptied one; held, not sent as a clear —
                // which would take the chip away under the typing.
                if (event.target.validity.badInput) return;
                choose('createdSince')(event.target.value);
              }}
              aria-label="Created since"
            />
          </label>
        </FilterChip>
      )}
    </>
  );
}

/**
 * The retired filters the address names, as chips of a work page's applied filters' row — each
 * with its value, its press opening its chip in the toolbar (`open`, by the chip's name), its ✕
 * dropping the parameter. The toolbar draws them by name alone.
 */
export function retiredFilterChips(
  params: URLSearchParams,
  choose: (name: string) => (value: Nullable<string>) => void,
  accountStates: readonly IFilterOption[],
  open: (label: string) => true
): ICompactChip[] {
  const createdSince = params.get('createdSince');
  const said: [string, string, Nullable<string>][] = [
    ['verification', 'Numbers', optionLabel(VERIFICATIONS, params.get('verification'))],
    ['registry', 'BrokerCheck', optionLabel(REGISTRY_STATES, params.get('registry'))],
    ['account', 'Account', optionLabel(accountStates, liveAccountFilter(params.get('account')))],
    ['disclosures', 'Disclosures', optionLabel(DISCLOSURE_STATES, params.get('disclosures'))],
    [
      'createdSince',
      'Created',
      createdSince === null ? null : createdSince === '' ? asSpelled(createdSince) : `since ${createdSince}`
    ]
  ];
  return said.flatMap(([param, label, value]) =>
    value === null
      ? []
      : [
          {
            key: param,
            chip: (
              <ParamChip
                words={`${label}: ${value}`}
                body={null}
                onPress={() => open(label)}
                onRemove={() => choose(param)(null)}
              />
            )
          }
        ]
  );
}
